import { useLoaderData, useNavigate } from "react-router-dom";

import { get } from "api";
import { RepeatMDFooter } from "atoms/repeatmd-footer/RepeatMDFooter";
import { ShopMembershipListItem } from "molecules/containers/shop-membership-list-item/ShopMembershipListItem";
import { queryClient } from "pages/Root";
import { useFeatureFlag } from "services/Flags";
import { getMembershipsWithDiscountInformation } from "services/ListingHelperService";
import { formatCurrency } from "toolbox/Money";
import { waitObj } from "toolbox/Promise";
import { defineLoader } from "toolbox/Router";
import { parseBillingPeriodName } from "utils/billingPeriod.helpers";

import { ShopListHeader } from "./ShopListHeader/ShopListHeader";

type LoaderData = Awaited<ReturnType<typeof shopMembershipsLoader>>;
export const shopMembershipsLoader = defineLoader(() => {
  const memberships = queryClient.fetchQuery(["/memberships"], () =>
    get("/memberships")
  );
  const categories = queryClient.fetchQuery(["shop/categories"], () =>
    get("/shop/categories")
  );
  const shopDiscounts = queryClient.fetchQuery(
    ["/shop/discounts"],
    () => get("/shop/discounts"),
    { staleTime: 1000 * 10 }
  );
  const customShopCategories = queryClient.fetchQuery(
    ["/shop/custom-categories/listings"],
    () => get("/shop/custom-categories/listings"),
    { staleTime: 1000 * 10 }
  );
  const user = queryClient.fetchQuery({
    queryKey: ["/user"],
    queryFn: () => get("/user"),
    staleTime: 1000 * 60 * 2,
  });

  return waitObj({
    memberships,
    categories,
    shopDiscounts,
    customShopCategories,
    user,
  });
});

const ShopMembershipsPage = () => {
  const { memberships, categories, shopDiscounts, customShopCategories, user } =
    useLoaderData() as LoaderData;
  const navigate = useNavigate();

  const flagBeautyBank = useFeatureFlag("membershipBeautyBank");
  const isFlexibleMembershipsEnabled = useFeatureFlag(
    "flexible_membership_terms"
  );

  const membershipsCategory = categories.find(
    (c) => c.defaultCategoryId === "memberships"
  );

  const membershipsWithDiscountInformation =
    getMembershipsWithDiscountInformation(
      memberships,
      shopDiscounts,
      customShopCategories
    );

  return (
    <div className="flex flex-col bg-two">
      {membershipsCategory && (
        <ShopListHeader
          title={membershipsCategory.title}
          subtitle={membershipsCategory.description}
          bgGradient
          // TODO(CAA-158) implement a backgroundImageUrl
          backgroundColor="rgb(var(--repeatmd-brand-color))"
          backgroundPattern
        />
      )}
      <div className="flex flex-col px-4 py-5">
        <div className="flex flex-col gap-5">
          {membershipsWithDiscountInformation.map((membership) => {
            const includedList = membership.membershipBenefits.map(
              (benefit) => benefit.description
            );
            if (flagBeautyBank) {
              const beautyBankCredit = membership.membershipRewardGroups?.find(
                (item) =>
                  item.beautyBank?.creditAmount !== null &&
                  item.membershipRewards.length === 0
              )?.beautyBank?.creditAmount;

              if (beautyBankCredit !== null && beautyBankCredit !== undefined) {
                includedList.unshift(
                  `Bank ${formatCurrency(
                    beautyBankCredit,
                    0
                  )} to spend on any service`
                );
              }
            }
            return (
              <ShopMembershipListItem
                key={membership.id}
                membershipId={membership.id}
                title={membership.name}
                includedList={includedList}
                imageUrl={membership.imageUrl}
                price={
                  isFlexibleMembershipsEnabled
                    ? membership.billingPeriodCost
                    : membership.monthlyCost
                }
                billingPeriodInterval={parseBillingPeriodName(
                  membership.billingPeriodInterval
                )}
                billingPeriodIntervalCount={
                  membership.billingPeriodIntervalCount
                }
                onClick={() => {
                  navigate(`/pdp/membership/${membership.id}`);
                }}
                discount={membership.discountText}
                customColor={membership.customColor}
                guestMode={user.isGuest}
              />
            );
          })}
        </div>
      </div>
      <RepeatMDFooter />
    </div>
  );
};

export default ShopMembershipsPage;
